import { GetAssignmentsForAssignableResponse } from '@eigtech/assignments-types'
import { ArrayElement } from '@eigtech/function-utils'
import { Job } from '@eigtech/restoration-types'
import { ensureClaimCorn } from '@eigtech/shared-corn-helper'
import { useMutation, useQueryClient } from '@eigtech/ui-shared-api'
import {
  assign,
  assignmentsQueryKeys,
  useInvalidateAssignmentsForAssignable,
} from '@eigtech/ui-shared-assignments'
import { getLog } from '@eigtech/ui-shared-logging'
import { useCallback } from 'react'
import { makeClaimAssignment } from './assignEntityToClaim'

const log = getLog()

export function useOptimisticallyUpdateAssignedClaimJob() {
  const queryClient = useQueryClient()
  const invalidateAssignmentsForClaim = useInvalidateAssignmentsForAssignable()

  const onMutate = useCallback(
    async ({ claimNumber, job }: { claimNumber: string; job: Job | undefined }) => {
      const type = 'job'

      const claimCorn = ensureClaimCorn(claimNumber)

      const assignmentQueryKey = assignmentsQueryKeys.assignable.assignments(claimCorn)
      await queryClient.cancelQueries({ queryKey: assignmentQueryKey })
      const previousAssignments =
        queryClient.getQueryData<GetAssignmentsForAssignableResponse>(assignmentQueryKey)

      if (previousAssignments) {
        const updatedAssignment = [...previousAssignments]

        if (job) {
          const claimAssignment = {
            ...makeClaimAssignment({
              assigneeRelationship: type,
              claimNumber,
              type: 'job',
              entity: job,
            }),
            assignmentDate: new Date().toISOString(),
            requestActor: 'user',
          } as ArrayElement<GetAssignmentsForAssignableResponse>
          updatedAssignment.push(claimAssignment)
        }

        queryClient.setQueryData<GetAssignmentsForAssignableResponse>(
          assignmentQueryKey,
          updatedAssignment
        )
      }

      return { previousAssignments }
    },
    [queryClient]
  )

  const onError = useCallback(
    (
      error: unknown,
      { claimNumber }: { claimNumber: string },
      context: Awaited<ReturnType<typeof onMutate>> | undefined
    ) => {
      log.error('could not set claim primary', { error })

      const claimCorn = ensureClaimCorn(claimNumber)

      const assignmentQueryKey = assignmentsQueryKeys.assignable.assignments(claimCorn)

      if (context?.previousAssignments) {
        queryClient.setQueryData<GetAssignmentsForAssignableResponse>(
          assignmentQueryKey,
          context.previousAssignments
        )
      }

      invalidateAssignmentsForClaim(ensureClaimCorn(claimNumber))
    },
    [invalidateAssignmentsForClaim, queryClient]
  )

  const onSuccess = useCallback(
    (claimNumber: string) => {
      const claimCorn = ensureClaimCorn(claimNumber)

      // invalidate after a few seconds on success
      // (it can take a lil bit for events on backend to catch up)
      setTimeout(() => {
        invalidateAssignmentsForClaim(ensureClaimCorn(claimCorn))
      }, 30000)
    },
    [invalidateAssignmentsForClaim]
  )

  return { onMutate, onError, onSuccess }
}

export type AssignJobToClaimProps = {
  claimNumber: string
  job: Job
}

export function useAssignJobToClaim() {
  const { onError, onMutate, onSuccess } = useOptimisticallyUpdateAssignedClaimJob()

  return useMutation({
    mutationFn: (props: AssignJobToClaimProps) => {
      return assign(
        makeClaimAssignment({
          assigneeRelationship: 'job',
          claimNumber: props.claimNumber,
          entity: props.job,
          type: 'job',
        })
      )
    },
    onMutate({ claimNumber, job }) {
      return onMutate({
        claimNumber,
        job,
      })
    },
    onSuccess(__, { claimNumber }) {
      onSuccess(claimNumber)
    },
    onError: (error, variables, context) => onError(error, variables, context),
  })
}
